import React, { useState, useEffect, useRef } from 'react'
import { Button, Box, Dialog, DialogActions, DialogContent, DialogTitle, Tabs, Tab, Grid, Badge, styled } from '@mui/material';
import BallotOutlinedIcon from "@mui/icons-material/BallotOutlined";
import { useTranslation } from 'react-i18next';
import TabPanel from '../../../TabPanel';
import FieldPortioning from './FieldPortioning';
import Replacement from './Replacement';
import Stripping from './Stripping';
import AlphaMask from './AlphaMask';
import Conversion from './Conversion';
import { lgDocPath } from '../../../../constants/languageDocPaths';
import { useSelector } from 'react-redux';
import { normalizeWorkflowReplaceForPersistence } from '../../../../utils/utilFunctions';
import Split from "react-split";
import { LuReplace } from "react-icons/lu";
import { MdOutlinePattern } from 'react-icons/md';
import { FaCodeCompare, FaTextSlash } from "react-icons/fa6";

const AdvancedProperties = ({ selectedNode, updatedSelectedNodeWithAdvanceProperties, disabled, isTradingPartnerLookup }) => {
    const { t } = useTranslation();
    const { baseUrl, lang } = useSelector((state) => state.queryParams);
    const [currTab, setCurrTab] = useState(0);
    const [open, setOpen] = useState(false);
    const [docPath, setDocPath] = useState('adv_field_portioning.html');
    const [clear, setClear] = useState(false);
    const [content, setContent] = useState(null);
    const [replacements, setReplacements] = useState([]);
    const [strips, setStrips] = useState([]);
    const [changed, setChanged] = useState(false);
    const [replaceBadge, setReplaceBadge] = useState(0);
    const [stripBadge, setStripBadge] = useState(0);
    const [alphaMaskBadge, setAlphaMaskBadge] = useState(0);
    const [conversionBadge, setConversionBadge] = useState(0);
    const [portioningBadge, setPortioningBadge] = useState(0);
    const [numOfAdvancedProperties, setNumOfAdvancedProperties] = useState(0);
    const [newStrips, setNewStrips] = useState([]);
    const [newReplacements, setNewReplacements] = useState([]);
    const [replaceModal, setReplaceModal] = useState('');
    const [stripModal, setStripModal] = useState('');
    const [fieldPortioningModal, setFieldPortioningModal] = useState('')
    const replaceRef = useRef('');
    const stripRef = useRef('');
    const fieldPortioningRef = useRef('');
    const handleTabChange = (event, newValue) => {
        setCurrTab(newValue);
    };

    const handleHelpForAlpha = () => {
        switch (currTab) {
            case 0:
                setDocPath('adv_field_portioning.html')
                break;

            case 1:
                setDocPath('adv_replacements.html')
                break;

            case 2:
                setDocPath('adv_stripping.html')
                break;

            case 3:
                setDocPath('adv_alphamask.html')
                break;

            case 4:
                setDocPath('adv_conversion_functions.html')
                break;

            default:
                setDocPath('adv_field_portioning.html')
                break;
        }
    }

    const handleHelp = () => {
        switch (currTab) {
            case 0:
                setDocPath('adv_field_portioning.html')
                break;

            case 1:
                setDocPath('adv_alphamask.html')
                break;

            case 2:
                setDocPath('adv_conversion_functions.html')
                break;

            default:
                setDocPath('adv_field_portioning.html')
                break;
        }
    }

    const handReplaceAndStrip = (val) => {
        let newStr = '';
        // Update ref values directly with the provided values
        if (val?.type === 'REPLACE') {
            replaceRef.current = val.replace;
            setNewReplacements(val.replace)
        }
        if (val?.type === 'STRIP') {
            stripRef.current = val.strip;
            setNewStrips(val?.strip);
        }

        // Construct newStr using the latest ref values
        if (replaceRef.current && stripRef.current) {
            newStr = `${replaceRef.current}${stripRef.current}`;
        } else if (replaceRef.current) {
            newStr = replaceRef.current;
        } else if (stripRef.current) {
            newStr = stripRef.current;
        }
        if (newStr !== '') okHandler({ type: "REPLACE", value: newStr });
    };

    const okHandler = (val) => {
        if (content && Object.keys(content).includes(val?.type)) {
            if (val?.value !== '') {
                setContent(prevContent => ({
                    ...prevContent,
                    [val.type]: val.value
                }));
            } else {
                // If value is empty, remove the property
                setContent(prevContent => {
                    const newContent = { ...prevContent };
                    delete newContent[val.type];
                    return newContent;
                });
            }
        } else {
            if (val?.value !== '') {
                setContent(prevContent => ({
                    ...(prevContent || {}),
                    [val.type]: val.value
                }));
            }
        }
    }

    const checkSomethingChanged = (data) => {
        let changed = false;
        for (const key in data) {
            const dataValue = data[key];
            if (dataValue && dataValue !== '--Select--' && dataValue !== 'NONE') {
                changed = true;
                break;
            } else {
                changed = false;
            }
        }
        setChanged(changed);
        return { value: changed };
    };

    const update = () => {
        const rawReplace = `${newStrips}${newReplacements}`;
        const newContent = {
            ...(content || {}),
            REPLACE: rawReplace ? normalizeWorkflowReplaceForPersistence(rawReplace) : rawReplace,
        };
        const obj = checkSomethingChanged(newContent);
        const updatedObj = { ...newContent, CHANGED: obj?.value };
        updatedSelectedNodeWithAdvanceProperties(updatedObj);
        
        setTimeout(() => {
            if (selectedNode?.ADVANCE) handleDefaultNode({...selectedNode.ADVANCE, ...updatedObj});
            else handleDefaultNode({...selectedNode, ...updatedObj});
        }, 0);
        
        closeModal();
    }

    const openModal = () => {
        setDocPath('adv_field_portioning.html');
        setOpen(true);
    }

    const closeModal = () => {
        setDocPath('adv_field_portioning.html');
        setOpen(false);
        // Reset content state when closing modal
        setContent(null);
    }

    const handleByFilterType = () => {
        if (selectedNode) {
            updatedSelectedNodeWithAdvanceProperties(selectedNode)
        }
    }

    const clearAdvancedProperties = (node) => {
        if (node) {
            const properties = ['SUBSTRING', 'REPLACE', 'MASK', 'FUNCTION', 'CHANGED'];

            const count = properties.reduce((total, prop) => {
                if (node[prop]) {
                    total++;
                }
                return total;
            }, 0);
            if (count === 1 || count > 1) {
                const propChanged = {};
                properties.forEach(property => {
                    if (node[property] !== undefined) {
                        propChanged[property] = ''
                    }
                });
                setContent(null);
                setReplacements([]);
                setStrips([]);
                stripRef.current = '';
                replaceRef.current = '';
                setNumOfAdvancedProperties(0);
                updatedSelectedNodeWithAdvanceProperties(propChanged);
            }
        }
    }

    const clearContent = () => {
        if (selectedNode?.ADVANCE) clearAdvancedProperties(selectedNode?.ADVANCE);
        else clearAdvancedProperties(selectedNode);
    }

    const updateLength = (val) => {
        if (val.type === 'REPLACE' && replaceRef !== null) {
            setReplaceBadge(val.len);
        }
        if (val.type === 'STRIP' && replaceRef !== null) {
            setStripBadge(val.len);
        }
        if (val.type === 'MASK') setAlphaMaskBadge(val.len);
        if (val.type === 'FUNCTION') setConversionBadge(val.len);
        if (val.type === 'SUBSTRING') setPortioningBadge(val.len);
    }

    const handleDefaultNode = (node) => {
        let total = 0;
        if (node?.REPLACE && node?.REPLACE !== '') {
            const allStrings = node?.REPLACE.split("%1E");
            const replaces = [];
            const strip = [];
            for (let i = 0; i < allStrings?.length; i++) {
                const item = allStrings[i];
                if (item?.endsWith("%1F")) {
                    if (item !== '') strip.push(item);
                } else {
                    if (item !== '') replaces.push(item);
                }
                if (item !== '') total += 1;
            }
            setStrips(strip);
            setReplacements(replaces);
            setReplaceBadge(replaces.length !== 0 ? replaces.length - 1 : replaces.length);
            setStripBadge(strip.length);
            

            if (node?.SUBSTRING?.length) setCurrTab(0);
            else if (replaces?.length) setCurrTab(1);
            else if (strip?.length) setCurrTab(2);
            else if (node?.MASK) setCurrTab(3);
            else if (node?.FUNCTION) setCurrTab(4);
            else setCurrTab(0);
        } else {
            setStrips([]);
            setReplacements([]);
            setReplaceBadge(0);
            setStripBadge(0);
            if (node?.SUBSTRING?.length) setCurrTab(0);
            else if (node?.MASK) setCurrTab(3);
            else if (node?.FUNCTION) setCurrTab(4);
            else setCurrTab(0);
            // setNumOfAdvancedProperties(0);
        }
        if (node?.REPLACE ||
            (node?.SUBSTRING && node?.SUBSTRING !== 'NONE') ||
            (node?.FUNCTION && node?.FUNCTION !== '--Select--') ||
            node?.MASK) {
            setChanged(true);
        } else {
            setChanged(false);
            setReplaceBadge(0);
            setStripBadge(0);
            // setNumOfAdvancedProperties(0); 
        }
        if (node?.MASK) {
            setAlphaMaskBadge(1);
            total += 1;
        }
        else setAlphaMaskBadge(0);
        if (node?.FUNCTION) {
            setConversionBadge(1);
            total += 1;
        }
        else setConversionBadge(0);
        if (node?.SUBSTRING && node?.SUBSTRING !== 'NONE') {
            setPortioningBadge(1);
            total += 1;
        }
        else setPortioningBadge(0);

        setNumOfAdvancedProperties(total);

        //???
        // if (node?.CHANGED) {
        //     setNumOfAdvancedProperties(total);
        // } else {
        //     setContent(null);
        //     setNumOfAdvancedProperties(0);
        // }
    }

    const handleReplacementHelpModal = (val) => {
        setDocPath(val);
    }

    const handleStripHelpModal = (val) => {
        setDocPath(val);
    }
    
    const handleFieldPortioningHelpModal = (val) => {
        setDocPath(val)
    }
    useEffect(() => {
      if (open) {
        const isAlphaField = isTradingPartnerLookup || selectedNode?.FIELDTYPE === "ALPHA";
        if (isAlphaField) {
          if (currTab === 0) {
            handleFieldPortioningHelpModal(fieldPortioningModal);
          } else if (currTab === 1) {
            if (replacements?.length === 0 && replaceBadge === 0)
              setDocPath("adv_replacements.html");
            else handleReplacementHelpModal(replaceModal);
          } else if (currTab === 2) {
            if (strips?.length === 0 && stripBadge === 0)
              setDocPath("adv_stripping.html");
            else handleStripHelpModal(stripModal);
          } else if (currTab === 3) {
            setDocPath("adv_alphamask.html");
          } else handleHelpForAlpha();
        } else {
         if (currTab === 0) {
          handleFieldPortioningHelpModal(fieldPortioningModal);
         } else handleHelp();
       } 

        stripRef.current = "";
        replaceRef.current = "";
      }
    }, [currTab, open, replaceModal, stripModal, fieldPortioningModal]);

    // useEffect(() => {
    //     if (open) {
    //         handleFieldPortioningHelpModal(fieldPortioningModal)
    //         // setDocPath('adv_field_portioning.html');
    //     }
    // }, [open]);

    useEffect(() => {
        if (selectedNode?.ADVANCE) handleDefaultNode(selectedNode?.ADVANCE);
        else handleDefaultNode(selectedNode);
        setNewReplacements([]);
        setNewStrips([]);

    }, [selectedNode?.key, open]);

    return (
        <>
            <ButtonBadge badgeContent={numOfAdvancedProperties} color="primary">
                <Button size="small" disabled={disabled} fullWidth variant="outlined" onClick={openModal}>
                    {t("BTN_ADV_PROPERTIES")} {numOfAdvancedProperties > 0 ? `(${t('SET')})` : ''}
                </Button>
            </ButtonBadge>

            <Dialog
                open={open}
                fullWidth={true}
                maxWidth={"lg"}>
                <DialogTitle style={{ borderBottom: "1px solid #e0e0e0" }}>
                    <div className="title">{t("TIT_ADV_PROP_DIALOG")}</div>
                </DialogTitle>
                <DialogContent
                    className="dialog-content"
                    sx={{ boxSizing: "border-box", height: "80vh", overflow: "hidden" }}>
                    <Split
                        className="split height-100 flex"
                        style={{ gap: "4px" }}
                        direction="horizontal"
                        gutterSize={4}
                        snapOffset={0}
                        dragInterval={4}
                        minSize={100}
                        sizes={[35, 65]}>
                        <Grid item xs={6}>
                            {docPath ? (
                                <iframe
                                    key={docPath}
                                    className="docFrame"
                                    title="advancePropertiesHelp"
                                    src={`${baseUrl}/docs/${lgDocPath[lang] || "en"}/Workbench/dialogs/${docPath}`}
                                ></iframe>
                            ) : (
                                ""
                            )}
                        </Grid>
                        <Grid item xs={8}>
                            <Box
                                style={{
                                    boxSizing: "border-box",
                                    height: "100%",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "5px",
                                }}>
                                <Box sx={{ border: 1, borderColor: "divider" }}>
                                    {(isTradingPartnerLookup || selectedNode?.FIELDTYPE === "ALPHA") ? (
                                        <Tabs
                                            sx={{ backgroundColor: "#fff", display: "flex" }}
                                            value={currTab}
                                            onChange={handleTabChange}>
                                            <Tab
                                                label={
                                                    <TabBadge badgeContent={portioningBadge} color="primary">
                                                        <div
                                                            className="flex align-start"
                                                            style={{ width: "100%" }}>
                                                            <BallotOutlinedIcon
                                                                size="small"
                                                                className="margin-right-4"
                                                            />
                                                            <span style={{ marginTop: "3px" }}>
                                                                {t("TAB_FLD_PORTIONING")}
                                                            </span>
                                                        </div>
                                                    </TabBadge>
                                                }
                                                sx={{ flex: 1 }}
                                            />
                                            <Tab
                                                label={
                                                    <TabBadge badgeContent={replaceBadge} color="primary">
                                                        <div
                                                            className="flex align-start"
                                                            style={{ width: "100%" }}
                                                        >
                                                            <LuReplace
                                                                size={"20px"}
                                                                className='margin-right-4'
                                                            />
                                                            <span style={{ marginTop: "3px" }}>
                                                                {t("TAB_REPLACEMENT")}
                                                            </span>
                                                        </div>
                                                    </TabBadge>
                                                }
                                                sx={{ flex: 1 }}
                                            />
                                            <Tab
                                                label={
                                                    <TabBadge badgeContent={stripBadge} color="primary">
                                                        <div
                                                            className="flex align-start"
                                                            style={{ width: "100%" }}>
                                                            <FaTextSlash size={"20px"}
                                                                className='margin-right-4'
                                                            />

                                                            <span style={{ marginTop: "3px" }}>
                                                                {t("TAB_STRIPPING")}
                                                            </span>

                                                        </div>
                                                    </TabBadge>
                                                }
                                                sx={{ flex: 1 }}
                                            />
                                            <Tab
                                                label={
                                                    <TabBadge badgeContent={alphaMaskBadge} color="primary">
                                                        <div
                                                            className="flex align-start"
                                                            style={{ width: "100%" }}>
                                                            <MdOutlinePattern size={"20px"} className='margin-right-4'/>
                                                            <span style={{ marginTop: "3px" }}>
                                                                {t("TAB_ALPHA_MSK")}
                                                            </span>
                                                        </div>
                                                    </TabBadge>
                                                }
                                                sx={{ flex: 1 }}
                                            />
                                            <Tab
                                                label={
                                                    <TabBadge badgeContent={conversionBadge} color='primary'>
                                                        <div
                                                            className="flex align-start"
                                                            style={{ width: "100%" }}>
                                                            <FaCodeCompare
                                                                size={"17px"} className='margin-right-4'
                                                            />
                                                            <span style={{ marginTop: "3px" }}>
                                                                {t("TAB_CONVERSION")}
                                                            </span>
                                                        </div>
                                                    </TabBadge>
                                                }
                                                sx={{ flex: 1 }}
                                            />
                                        </Tabs>
                                    ) : (
                                        <Tabs
                                            sx={{ backgroundColor: "#fff", display: "flex" }}
                                            value={currTab}
                                            onChange={handleTabChange}>
                                            <Tab
                                                label={
                                                    <TabBadge badgeContent={portioningBadge} color="primary">
                                                        <div
                                                            className="flex align-start"
                                                            style={{ width: "100%" }}>
                                                            <BallotOutlinedIcon
                                                                size="small"
                                                                className="margin-right-4"
                                                            />
                                                            <span style={{ marginTop: "3px" }}>
                                                                {t("TAB_FLD_PORTIONING")}
                                                            </span>
                                                        </div>
                                                    </TabBadge>
                                                }
                                                sx={{ flex: 1 }}
                                            />
                                            <Tab
                                                label={
                                                    <TabBadge badgeContent={alphaMaskBadge} color="primary">
                                                        <div
                                                            className="flex align-start"
                                                            style={{ width: "100%" }}>
                                                            <BallotOutlinedIcon
                                                                size="small"
                                                                className="margin-right-4"
                                                            />
                                                            <span style={{ marginTop: "3px" }}>
                                                                {t("TAB_ALPHA_MSK")}
                                                            </span>
                                                        </div>
                                                    </TabBadge>
                                                }
                                                sx={{ flex: 1 }}
                                            />
                                            <Tab
                                                label={
                                                    <TabBadge badgeContent={conversionBadge} color='primary'>
                                                        <div
                                                            className="flex align-start"
                                                            style={{ width: "100%" }}>
                                                            <BallotOutlinedIcon
                                                                size="small"
                                                                className="margin-right-4"
                                                            />
                                                            <span style={{ marginTop: "3px" }}>
                                                                {t("TAB_CONVERSION")}
                                                            </span>
                                                        </div>
                                                    </TabBadge>
                                                }
                                                sx={{ flex: 1 }}
                                            />
                                        </Tabs>
                                    )}
                                </Box>
                                {(isTradingPartnerLookup || selectedNode?.FIELDTYPE === "ALPHA") ? (
                                    <div
                                        style={{
                                            height: "calc(100% - 34px)", // this tab's sibling is 34px height.
                                            boxSizing: "border-box",
                                            overflow: "auto",
                                        }}>
                                        <Box className="height-100-tab width-100">
                                            <TabPanel
                                                className="height-100 width-100"
                                                value={currTab}
                                                index={0}>
                                                <FieldPortioning
                                                    okHandler={okHandler}
                                                    clear={clear}
                                                    setClear={setClear}
                                                    selectedNode={selectedNode}
                                                    updateLength={updateLength}
                                                    setHelpDocPath={(value) => setFieldPortioningModal(value)}
                                                    currTab={currTab}
                                                />
                                            </TabPanel>
                                            <TabPanel
                                                className="height-100 width-100"
                                                value={currTab}
                                                index={1}>
                                                <Replacement
                                                    setHelpDocPath={(value) => setReplaceModal(value)}
                                                    selectedNode={selectedNode}
                                                    handReplaceAndStrip={handReplaceAndStrip}
                                                    replacements={replacements}
                                                    updateLength={updateLength}
                                                    strips={strips}
                                                    currTab={currTab}
                                                />
                                            </TabPanel>
                                            <TabPanel
                                                className="height-100 width-100"
                                                value={currTab}
                                                index={2}>
                                                <Stripping
                                                    setHelpDocPath={(value) => setStripModal(value)}
                                                    selectedNode={selectedNode}
                                                    handReplaceAndStrip={handReplaceAndStrip}
                                                    strips={strips}
                                                    updateLength={updateLength}
                                                    currTab={currTab}
                                                />
                                            </TabPanel>
                                            <TabPanel
                                                className="height-100 width-100"
                                                value={currTab}
                                                index={3}>
                                                <AlphaMask
                                                    okHandler={okHandler}
                                                    clear={clear}
                                                    setClear={setClear}
                                                    selectedNode={selectedNode}
                                                    updateLength={updateLength}
                                                />
                                            </TabPanel>
                                            <TabPanel
                                                className="height-100 width-100"
                                                value={currTab}
                                                index={4}>
                                                <Conversion
                                                    okHandler={okHandler}
                                                    clear={clear}
                                                    setClear={setClear}
                                                    selectedNode={selectedNode}
                                                    updateLength={updateLength}
                                                />
                                            </TabPanel>
                                        </Box>
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            height: "calc(100% - 34px)", // this tab's sibling is 34px height.
                                            boxSizing: "border-box",
                                            overflow: "auto",
                                        }}
                                    >
                                        <Box className="height-100-tab width-100">
                                            <div>
                                                <TabPanel
                                                    className="height-100 width-100"
                                                    value={currTab}
                                                    index={0}>
                                                    <FieldPortioning
                                                        okHandler={okHandler}
                                                        clear={clear}
                                                        setClear={setClear}
                                                        selectedNode={selectedNode}
                                                        updateLength={updateLength}
                                                        setHelpDocPath={(value) => setFieldPortioningModal(value)}
                                                        currTab={currTab}
                                                    />
                                                </TabPanel>
                                                <TabPanel
                                                    className="height-100 width-100"
                                                    value={currTab}
                                                    index={1}>
                                                    <AlphaMask
                                                        okHandler={okHandler}
                                                        clear={clear}
                                                        setClear={setClear}
                                                        selectedNode={selectedNode}
                                                        updateLength={updateLength}
                                                    />
                                                </TabPanel>
                                                <TabPanel
                                                    className="height-100 width-100"
                                                    value={currTab}
                                                    index={2}>
                                                    <Conversion
                                                        okHandler={okHandler}
                                                        clear={clear}
                                                        setClear={setClear}
                                                        selectedNode={selectedNode}
                                                        updateLength={updateLength}
                                                    />
                                                </TabPanel>
                                            </div>
                                        </Box>
                                    </div>
                                )}
                            </Box>
                        </Grid>
                    </Split>
                </DialogContent>
                <DialogActions>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                        }}>
                        <Box sx={{ ml: "15px" }}>
                            {/* <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleHelp()}>
                  {t("HELP")}
                </Button> */}
                        </Box>
                        <Box sx={{ display: "flex", gap: "10px" }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    clearContent();
                                    setConversionBadge(0);
                                    setAlphaMaskBadge(0);
                                    setPortioningBadge(0);
                                    setReplaceBadge(0);
                                    setStripBadge(0);
                                    setClear(true);
                                    setNumOfAdvancedProperties(0);
                                    closeModal();
                                }}>
                                {t("BTN_CLEAR_ADVANCED_PROPS")}
                            </Button>
                            <Button variant="outlined" size="small" onClick={update}>
                                {t("BTN_OK")}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    closeModal();
                                    handleByFilterType();
                                }}>
                                {t("BTN_CANCEL")}
                            </Button>
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
}

export const TabBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -10,
        top: 2,
        border: `1px solid #d4d4d4`,
        // backgroundColor: "#FFFFFF00",
        padding: '0px 4px',
        height: "16px",
        width: "16px",
        minWidth: "16px"
    },
}));

export const ButtonBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -2,
        top: 2,
        border: `1px solid #d4d4d4`,
        padding: '0px 4px',
        height: '16px',
        width: '16px',
        minWidth: '16px',
    },
    width: '100%'
}));

export default AdvancedProperties